// Personas de una jornada de un multievento en un paso.
//
// Un número y "Aplicar": el servidor fija las personas de la jornada, pone ese
// número en las líneas de menú que tenían el anterior, recalcula totales,
// suplemento mínimo de la jornada, tareas y cobro (PUT
// /presupuestos/{id}/jornadas/{jid}/personas). Las líneas que tenían otro
// número se dejan y se avisa: "3 líneas tenían un número distinto: revísalas".
//
// Props:
// - presupuestoId, jornada ({id, num_personas}), etiqueta (texto opcional)
// - onActualizado(presupuesto, respuesta): con el presupuesto que devuelve el servidor
// - compacto: sin la explicación de debajo
import React, { useEffect, useState } from 'react';
import { presupuestosAPI } from '../../api/api';
import { toast } from '../../hooks/use-toast';

const PersonasJornada = ({ presupuestoId, jornada, etiqueta = 'Personas de la jornada', onActualizado, compacto = false }) => {
  const actual = Number(jornada?.num_personas) || 0;
  const [valor, setValor] = useState(String(actual || ''));
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);

  useEffect(() => { setValor(String(actual || '')); }, [actual, jornada?.id]);

  const numero = parseInt(valor, 10);
  const valido = Number.isFinite(numero) && numero > 0;
  const cambiado = valido && numero !== actual;

  const aplicar = async () => {
    if (!cambiado || guardando || !jornada?.id) return;
    setGuardando(true);
    setAviso(null);
    try {
      const { data } = await presupuestosAPI.cambiarPersonasJornada(presupuestoId, jornada.id, numero, true);
      setAviso(data?.aviso || null);
      toast({
        title: 'Personas de la jornada cambiadas',
        description: `${actual} a ${numero} personas; ${data?.lineas_actualizadas || 0} ${data?.lineas_actualizadas === 1 ? 'línea actualizada' : 'líneas actualizadas'}.`,
      });
      if (onActualizado) onActualizado(data?.presupuesto || null, data);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'No se han cambiado las personas',
        description: error?.response?.data?.detail || error.message,
      });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="flex flex-col gap-1" data-testid={`personas-jornada-${jornada?.id}`}>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs font-semibold text-[#5B6B64]">
          {etiqueta}
          <input
            type="number"
            min="1"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') aplicar(); }}
            disabled={guardando}
            className="w-24 h-9 rounded-lg border border-[#CBD3CF] px-2 text-sm text-[#16221D] focus:outline-none focus:border-duetto-primary"
            data-testid={`personas-jornada-input-${jornada?.id}`}
          />
        </label>
        <button
          type="button"
          onClick={aplicar}
          disabled={!cambiado || guardando}
          className="h-9 px-3 rounded-lg bg-duetto-primary text-white text-sm font-semibold hover:bg-opacity-90 disabled:opacity-40"
          data-testid={`personas-jornada-aplicar-${jornada?.id}`}
        >
          {guardando ? 'Aplicando...' : 'Aplicar'}
        </button>
      </div>
      {!compacto && (
        <p className="text-xs text-[#5B6B64]">
          Cambia la jornada y las líneas que tenían {actual || 'el mismo número de'} personas; se recalculan importes, tareas y cobro.
        </p>
      )}
      {aviso && (
        <p className="text-xs rounded-lg border px-2.5 py-1.5 bg-[#FDF0DC] border-[#F4D9AE] text-[#7A3A06]" data-testid="personas-jornada-aviso">
          {aviso}
        </p>
      )}
    </div>
  );
};

export default PersonasJornada;
